<template>
  <div class="pilih" :class="{ 'pilih--nonaktif': disabled }">
    <button
      ref="pemicu"
      type="button"
      class="form-select pilih__pemicu"
      :class="{ 'is-invalid': invalid }"
      :disabled="disabled"
      :aria-expanded="String(terbuka)"
      aria-haspopup="listbox"
      @click="alihkan"
      @keydown.down.prevent="buka(true)"
    >
      <span :class="{ 'pilih__kosong': !terpilih }">
        {{ terpilih ? label(terpilih) : placeholder }}
      </span>
    </button>

    <!--
      Panel DIPINDAHKAN ke <body> lewat Teleport.
      Dilengkapi backdrop agar klik di luar menutup dropdown dengan aman
      tanpa memicu race-condition event touch/click pada browser seluler.
    -->
    <Teleport to="body">
      <div v-if="terbuka" class="pilih__wadah-teleport">
        <!-- Backdrop transparan pada desktop / semi-transparan pada ponsel -->
        <div
          class="pilih__latar"
          :class="{ 'pilih__latar--mobile': isMobile }"
          @click="tutup"
          @pointerdown.stop
        ></div>

        <div
          ref="panel"
          class="pilih__panel"
          :class="{ 'pilih__panel--mobile': isMobile }"
          :style="gaya"
          role="listbox"
        >
          <div class="pilih__cari">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input
              ref="kotakCari"
              v-model="kata"
              type="search"
              class="pilih__input"
              :placeholder="cariPlaceholder"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              @keydown.down.prevent="geser(1)"
              @keydown.up.prevent="geser(-1)"
              @keydown.enter.prevent="pilihSorotan"
              @keydown.esc.prevent="tutup"
            />
            <button
              v-if="kata"
              type="button"
              class="pilih__bersihkan"
              aria-label="Bersihkan kata kunci"
              @click="kata = ''"
            >
              <i class="bi bi-x-circle-fill"></i>
            </button>
            <button
              v-if="isMobile"
              type="button"
              class="pilih__tutup-mobile"
              aria-label="Tutup"
              @click="tutup"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <ul class="pilih__daftar">
            <li v-if="allowEmpty">
              <button
                type="button"
                class="pilih__item"
                :class="{ 'is-terpilih': modelValue === '' || modelValue === null }"
                @click="pilih(null)"
              >
                {{ emptyLabel }}
              </button>
            </li>

            <li v-for="(o, i) in tersaring" :key="nilai(o)">
              <button
                type="button"
                class="pilih__item"
                :class="{
                  'is-sorot': i === sorotan,
                  'is-terpilih': nilai(o) === modelValue,
                }"
                @click="pilih(o)"
                @mouseenter="sorotan = i"
              >
                {{ label(o) }}
              </button>
            </li>

            <li v-if="!tersaring.length" class="pilih__kosong-hasil">
              Tidak ada yang cocok dengan &ldquo;{{ kata }}&rdquo;.
            </li>
          </ul>

          <div v-if="options.length > 12" class="pilih__kaki">
            {{ tersaring.length }} dari {{ options.length }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
/**
 * Pemilih dengan kotak pencarian cepat (PilihCari).
 *
 * MENGATASI BUG KEYBOARD AUTOCLOSE PADA PONSEL:
 * Pada versi sebelumnya, event listener 'resize' dan 'scroll' dipasang dengan callback
 * langsung memanggil `this.tutup`.
 *
 * Di ponsel (baik mode mobile maupun mode desktop), begitu pengguna menyentuh kotak input,
 * browser memunculkan virtual keyboard. Pemunculan virtual keyboard mengubah tinggi viewport
 * dan memicu event 'resize' dan 'scroll' otomatis. Akibatnya, pemilih langsung menutup sendiri
 * dan input dihancurkan dari DOM yang memaksa keyboard seketika tertutup (autoclose).
 *
 * Penanganan:
 *   1. Event 'resize' dan 'scroll' memanggil `this.hitungPosisi()` untuk menyesuaikan posisi/tinggi,
 *      BUKAN menutup dropdown.
 *   2. Di layar ponsel (< 768px), panel ditampilkan sebagai modal terpusat di atas area keyboard.
 *   3. Ditambahkan backdrop terisolasi sehingga menutup hanya terjadi saat pengguna benar-benar
 *      mengeklik di luar panel atau tombol batal.
 */
export default {
  name: 'PilihCari',

  props: {
    modelValue: { type: [String, Number, null], default: null },
    options: { type: Array, default: () => [] },
    labelKey: { type: String, default: 'name' },
    valueKey: { type: String, default: 'id' },
    placeholder: { type: String, default: 'Pilih…' },
    cariPlaceholder: { type: String, default: 'Ketik untuk mencari…' },
    allowEmpty: { type: Boolean, default: false },
    emptyLabel: { type: String, default: 'Semua' },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      terbuka: false,
      kata: '',
      sorotan: 0,
      isMobile: false,
      gaya: {
        position: 'fixed',
        top: '-9999px',
        left: '-9999px',
      },
    };
  },

  computed: {
    terpilih() {
      return this.options.find((o) => this.nilai(o) === this.modelValue) || null;
    },

    tersaring() {
      const k = this.kata.trim().toLowerCase();
      if (!k) return this.options;
      return this.options.filter((o) => this.label(o).toLowerCase().includes(k));
    },
  },

  beforeUnmount() {
    this.lepasPendengar();
  },

  methods: {
    label(o) {
      return String(o?.[this.labelKey] ?? '');
    },

    nilai(o) {
      return o?.[this.valueKey];
    },

    alihkan() {
      this.terbuka ? this.tutup() : this.buka();
    },

    async buka() {
      if (this.disabled) return;

      this.terbuka = true;
      this.kata = '';
      this.sorotan = 0;

      await this.$nextTick();
      this.hitungPosisi();

      // Jeda mikro agar DOM Teleport terpasang stabil sebelum input difokuskan
      setTimeout(() => {
        if (this.terbuka && this.$refs.kotakCari) {
          this.$refs.kotakCari.focus({ preventScroll: true });
        }
      }, 60);

      window.addEventListener('resize', this.saatResize, { passive: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.saatResize, { passive: true });
        window.visualViewport.addEventListener('scroll', this.saatResize, { passive: true });
      }
      window.addEventListener('scroll', this.saatGulir, true);
    },

    tutup() {
      if (!this.terbuka) return;

      this.terbuka = false;
      this.lepasPendengar();
    },

    lepasPendengar() {
      window.removeEventListener('resize', this.saatResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.saatResize);
        window.visualViewport.removeEventListener('scroll', this.saatResize);
      }
      window.removeEventListener('scroll', this.saatGulir, true);
    },

    saatResize() {
      if (!this.terbuka) return;
      // Perbarui posisi dan tinggi maksimum saat keyboard muncul atau orientasi layar berubah
      this.hitungPosisi();
    },

    saatGulir(e) {
      if (!this.terbuka) return;
      // Jangan ganggu jika scrolling terjadi di dalam daftar opsi
      if (this.$refs.panel?.contains(e.target)) return;

      // Jangan tutup jika pengguna sedang mengetik di kotak pencarian
      if (document.activeElement === this.$refs.kotakCari) {
        this.hitungPosisi();
        return;
      }

      this.hitungPosisi();
    },

    hitungPosisi() {
      const pemicu = this.$refs.pemicu;
      const panel = this.$refs.panel;
      if (!pemicu || !panel) return;

      const viewportWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth;
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const isMobileViewport = viewportWidth < 768;
      this.isMobile = isMobileViewport;

      if (isMobileViewport) {
        // Mode Ponsel: Tampilkan sebagai modal sheet di bagian atas ruang visual bebas keyboard
        const offsetTop = window.visualViewport ? window.visualViewport.offsetTop : 0;
        const tinggiMaks = Math.min(380, Math.max(180, viewportHeight - 24));
        const lebarModal = Math.min(460, viewportWidth - 24);

        this.gaya = {
          position: 'fixed',
          top: `${offsetTop + 12}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          width: `${lebarModal}px`,
          maxHeight: `${tinggiMaks}px`,
          zIndex: 10001,
        };
        return;
      }

      // Mode Desktop: Menempel rapi pada tombol pemicu
      const r = pemicu.getBoundingClientRect();
      const jarak = 4;
      const tinggi = panel.offsetHeight || 260;

      const ruangBawah = viewportHeight - r.bottom;
      const keAtas = ruangBawah < Math.min(tinggi, 260) + jarak && r.top > ruangBawah;
      const tersedia = (keAtas ? r.top : ruangBawah) - jarak * 2;
      const lebar = Math.max(r.width, 240);

      let posKiri = r.left;
      if (posKiri + lebar > viewportWidth - 12) {
        posKiri = Math.max(12, viewportWidth - lebar - 12);
      }

      this.gaya = {
        position: 'fixed',
        width: `${lebar}px`,
        left: `${posKiri}px`,
        top: keAtas ? `${Math.max(jarak, r.top - tinggi - jarak)}px` : `${r.bottom + jarak}px`,
        transform: 'none',
        maxHeight: `${Math.max(180, Math.min(400, tersedia))}px`,
        zIndex: 10001,
      };
    },

    geser(arah) {
      if (!this.tersaring.length) return;

      this.sorotan = (this.sorotan + arah + this.tersaring.length) % this.tersaring.length;

      this.$nextTick(() => {
        this.$refs.panel
          ?.querySelector('.pilih__item.is-sorot')
          ?.scrollIntoView({ block: 'nearest' });
      });
    },

    pilihSorotan() {
      const o = this.tersaring[this.sorotan];
      if (o) this.pilih(o);
    },

    pilih(o) {
      this.$emit('update:modelValue', o ? this.nilai(o) : '');
      this.tutup();
      this.$refs.pemicu?.focus();
    },
  },
};
</script>

<style scoped>
.pilih { position: relative; }

.pilih__pemicu {
  width: 100%;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pilih--nonaktif .pilih__pemicu { cursor: not-allowed; }

.pilih__kosong { color: var(--text-muted, #9ca3af); }
</style>

<!--
  TIDAK scoped: panel dipindahkan ke <body> lewat Teleport, jadi ia tidak
  lagi punya atribut data-v komponen ini dan tidak bisa dijangkau gaya
  ber-scope.
-->
<style>
.pilih__wadah-teleport {
  position: relative;
  z-index: 10001;
}

.pilih__latar {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: transparent;
}

.pilih__latar--mobile {
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.pilih__panel {
  position: fixed;
  top: -9999px;
  left: -9999px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  background: var(--bg-surface, #fff);
  border: 1px solid var(--border-subtle, #e5e7eb);
  border-radius: 12px;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.pilih__panel--mobile {
  border: 1px solid var(--border-color, #d1d5db);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.35);
}

.pilih__cari {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.6rem;
  gap: 0.4rem;
  border-bottom: 1px solid var(--border-subtle, #e5e7eb);
  background: var(--bg-surface, #fff);
}

.pilih__cari i.bi-search {
  position: absolute;
  left: 1.1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted, #9ca3af);
  pointer-events: none;
  font-size: 0.95rem;
}

.pilih__input {
  flex: 1 1 auto;
  width: 100%;
  padding: 0.5rem 2.2rem 0.5rem 2.2rem;
  border: 1px solid var(--border-subtle, #e5e7eb);
  border-radius: 8px;
  background: var(--bg-sunken, #f9fafb);
  color: var(--text-primary, #111827);
  font-size: 0.95rem;
}

.pilih__input:focus {
  outline: 2px solid var(--accent, #ea580c);
  outline-offset: -1px;
}

.pilih__bersihkan {
  position: absolute;
  right: 3.2rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: var(--text-muted, #9ca3af);
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pilih__panel:not(.pilih__panel--mobile) .pilih__bersihkan {
  right: 1rem;
}

.pilih__tutup-mobile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: var(--bg-sunken, #f3f4f6);
  color: var(--text-secondary, #4b5563);
  cursor: pointer;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.pilih__daftar {
  list-style: none;
  margin: 0;
  padding: 0.25rem;
  overflow-y: auto;
  flex: 1 1 auto;
  -webkit-overflow-scrolling: touch;
}

.pilih__item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  text-align: left;
  padding: 0.55rem 0.75rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text-primary, #111827);
  font-size: 0.92rem;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color var(--transition-fast, 0.15s ease);
}

.pilih__item.is-sorot { background: var(--bg-hover, #f3f4f6); }

.pilih__item.is-terpilih {
  background: rgba(232, 72, 8, 0.12);
  font-weight: 600;
  color: var(--accent, #ea580c);
}

.pilih__kosong-hasil {
  padding: 1rem 0.8rem;
  font-size: 0.9rem;
  color: var(--text-secondary, #6b7280);
  text-align: center;
}

.pilih__kaki {
  padding: 0.4rem 0.7rem;
  border-top: 1px solid var(--border-subtle, #e5e7eb);
  font-size: 0.8rem;
  color: var(--text-muted, #9ca3af);
}
</style>
